import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { SimulatedPersonDto, VenueDto } from '@ivy-monorepo/shared'
import { adminService } from '../../../services/admin.service'

/** Demo location presets — defaults match Live Map / Add Venue Chicago focus. */
export type SpawnLocationPresetId = 'chicago-downtown' | 'chicago-wicker' | 'custom'

export type SpawnLocationPreset = {
  id: SpawnLocationPresetId
  label: string
  note: string
  latitude: number
  longitude: number
  radiusMeters: number
  northeastLatitude: number
  northeastLongitude: number
  southwestLatitude: number
  southwestLongitude: number
}

export const SPAWN_LOCATION_PRESETS: SpawnLocationPreset[] = [
  {
    id: 'chicago-downtown',
    label: 'Downtown Chicago (Loop)',
    note: 'Default demo area — Millennium Park / Loop (~2.5 km radius).',
    latitude: 41.8827,
    longitude: -87.6233,
    radiusMeters: 2500,
    northeastLatitude: 41.9,
    northeastLongitude: -87.6,
    southwestLatitude: 41.86,
    southwestLongitude: -87.65,
  },
  {
    id: 'chicago-wicker',
    label: 'Wicker Park / Division St',
    note: 'Near the seeded venue cluster on Division (~2 km radius).',
    latitude: 41.9,
    longitude: -87.64,
    radiusMeters: 2000,
    northeastLatitude: 41.915,
    northeastLongitude: -87.62,
    southwestLatitude: 41.885,
    southwestLongitude: -87.66,
  },
  {
    id: 'custom',
    label: 'Custom coordinates',
    note: 'Enter your own center, radius, or bounding box.',
    latitude: 41.8827,
    longitude: -87.6233,
    radiusMeters: 2500,
    northeastLatitude: 41.9,
    northeastLongitude: -87.6,
    southwestLatitude: 41.86,
    southwestLongitude: -87.65,
  },
]

export const DEFAULT_SPAWN_LOCATION_PRESET = SPAWN_LOCATION_PRESETS[0]

export type VenueOption = {
  publicId: string
  name: string
  address?: string | null
}

type VenueSearchSelectProps = {
  label: string
  hint?: string
  multiple?: boolean
  selected: VenueOption[]
  onChange: (venues: VenueOption[]) => void
  disabled?: boolean
}

export const VenueSearchSelect = ({
  label,
  hint,
  multiple = false,
  selected,
  onChange,
  disabled,
}: VenueSearchSelectProps) => {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<VenueOption[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    const onDocClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  useEffect(() => {
    const trimmed = query.trim()
    if (trimmed.length < 2) {
      setResults([])
      return
    }

    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    const timer = window.setTimeout(async () => {
      setLoading(true)
      try {
        const response = await adminService.getVenues(1, 12, trimmed, 'name', 'asc')
        if (controller.signal.aborted) return
        setResults(
          response.venues.map((venue: VenueDto) => ({
            publicId: venue.publicId,
            name: venue.name,
            address: venue.address ?? null,
          })),
        )
        setOpen(true)
      } catch {
        if (!controller.signal.aborted) {
          setResults([])
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false)
        }
      }
    }, 250)

    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [query])

  const selectedIds = useMemo(() => new Set(selected.map(v => v.publicId)), [selected])

  const pick = (venue: VenueOption) => {
    if (multiple) {
      if (selectedIds.has(venue.publicId)) {
        onChange(selected.filter(v => v.publicId !== venue.publicId))
      } else {
        onChange([...selected, venue])
      }
    } else {
      onChange([venue])
      setQuery('')
      setOpen(false)
    }
  }

  const remove = (publicId: string) => {
    onChange(selected.filter(v => v.publicId !== publicId))
  }

  return (
    <div className="form-group simulation-picker" ref={containerRef}>
      <span>{label}</span>
      {hint && <p className="simulation-field-note">{hint}</p>}
      {selected.length > 0 && (
        <div className="simulation-chips">
          {selected.map(venue => (
            <button
              key={venue.publicId}
              type="button"
              className="simulation-chip"
              disabled={disabled}
              onClick={() => remove(venue.publicId)}
              title="Remove"
            >
              {venue.name}
              <span aria-hidden>×</span>
            </button>
          ))}
        </div>
      )}
      <input
        value={query}
        disabled={disabled}
        onChange={e => setQuery(e.target.value)}
        onFocus={() => results.length > 0 && setOpen(true)}
        placeholder={multiple ? 'Search venues to add…' : 'Search venue by name…'}
        autoComplete="off"
      />
      {open && (results.length > 0 || loading) && (
        <ul className="simulation-picker-dropdown">
          {loading && <li className="simulation-picker-empty">Searching…</li>}
          {!loading &&
            results.map(venue => {
              const isSelected = selectedIds.has(venue.publicId)
              return (
                <li key={venue.publicId}>
                  <button type="button" onClick={() => pick(venue)} disabled={disabled}>
                    <strong>{venue.name}</strong>
                    {venue.address && <span>{venue.address}</span>}
                    {isSelected && <em>Selected</em>}
                  </button>
                </li>
              )
            })}
        </ul>
      )}
    </div>
  )
}

type PersonMultiSelectProps = {
  label: string
  hint?: string
  people: SimulatedPersonDto[]
  selectedPublicIds: string[]
  onChange: (publicIds: string[]) => void
  disabled?: boolean
}

export const PersonMultiSelect = ({
  label,
  hint,
  people,
  selectedPublicIds,
  onChange,
  disabled,
}: PersonMultiSelectProps) => {
  const selected = useMemo(() => new Set(selectedPublicIds), [selectedPublicIds])

  const toggle = useCallback(
    (publicId: string) => {
      if (selected.has(publicId)) {
        onChange(selectedPublicIds.filter(id => id !== publicId))
      } else {
        onChange([...selectedPublicIds, publicId])
      }
    },
    [onChange, selected, selectedPublicIds],
  )

  const selectAll = () => onChange(people.map(p => p.publicId))
  const clearAll = () => onChange([])

  return (
    <div className="form-group simulation-picker">
      <span>{label}</span>
      {hint && <p className="simulation-field-note">{hint}</p>}
      <div className="simulation-person-select-actions">
        <button type="button" className="simulation-link-btn" disabled={disabled || people.length === 0} onClick={selectAll}>
          Select all idle
        </button>
        <button type="button" className="simulation-link-btn" disabled={disabled || selectedPublicIds.length === 0} onClick={clearAll}>
          Clear
        </button>
      </div>
      <div className="simulation-person-list">
        {people.length === 0 ? (
          <p className="simulation-picker-empty">No idle active personas available.</p>
        ) : (
          people.map(person => {
            const checked = selected.has(person.publicId)
            return (
              <label key={person.publicId} className={`simulation-person-option${checked ? ' is-selected' : ''}`}>
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => toggle(person.publicId)}
                />
                <span className="simulation-person-option__name">
                  {person.firstName} {person.lastName}
                </span>
                <span className="simulation-person-option__meta">
                  {person.gender}
                  {person.vibeEmoji ? ` · ${person.vibeEmoji}` : ''}
                </span>
              </label>
            )
          })
        )}
      </div>
    </div>
  )
}
